import * as ToggleGroupRadix from '@radix-ui/react-toggle-group';
import clsx from 'clsx';
import React, { ComponentPropsWithoutRef } from 'react';
import { mergeProps } from 'src/core/utils';

import * as styles from './ToggleGroup.css';

export type ToggleItem = {
  value: string;
  children: React.ReactNode;
};

export type ToggleGroupProps = Omit<
  ToggleGroupRadix.ToggleGroupSingleProps,
  'type'
> & {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  options: ToggleItem[];
  disabledValues?: string[];
  disabled?: boolean;
  itemStyle?: ComponentPropsWithoutRef<typeof ToggleGroupRadix.Item>['style'];
  itemClassName?: string;
  itemSelectedClassName?: string;
  rovingFocus?: boolean;
  itemTransparent?: boolean;
};

export const ToggleGroup = ({
  value,
  defaultValue,
  onValueChange,
  options,
  disabledValues,
  disabled,
  itemStyle,
  itemClassName,
  itemSelectedClassName,
  itemTransparent,
  rovingFocus = true,
  ...props
}: ToggleGroupProps) => {
  return (
    <ToggleGroupRadix.Root
      className={styles.toggleGroup}
      type="single"
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      disabled={disabled}
      rovingFocus={rovingFocus}
      {...mergeProps(
        {
          className: styles.toggleGroup,
        },
        props
      )}
    >
      {options.map((item, i) => {
        const isItemDisabled =
          disabled || (disabledValues?.includes(item.value) ?? false);
        return (
          <ToggleGroupRadix.Item
            key={i}
            className={clsx(
              styles.toggleGroupItem,
              itemClassName,
              {
                [styles.toggleGroupItemTransparent]: itemTransparent,
                [styles.toggleGroupItemSelected]:
                  !itemSelectedClassName && item.value === value,
                [styles.toggleGroupItemDisabled]: isItemDisabled,
                [styles.toggleGroupItemSelectedDisabled]:
                  item.value === value && isItemDisabled,
              },
              item.value === value ? itemSelectedClassName : undefined
            )}
            disabled={isItemDisabled}
            style={itemStyle}
            value={item.value}
          >
            {item.children}
          </ToggleGroupRadix.Item>
        );
      })}
    </ToggleGroupRadix.Root>
  );
};
